import { format } from 'date-fns';
import { useState } from 'react';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    deltaTone,
    sameTimeDeltaPct,
} from '@apps/web/components/admin/outreach_observability/comparison_utils';
import {
    OUTREACH_REFRESH_INTERVAL_MS,
    type OutreachTabProps,
} from '@apps/web/components/admin/outreach_observability/constants';
import { KpiStatCard } from '@apps/web/components/admin/outreach_observability/kpi_stat_card';
import { OutreachUrgencySelect } from '@apps/web/components/admin/outreach_observability/outreach_urgency_select';
import {
    type KpiRunMetric,
    RunsDrillDialog,
    type RunsDrillFilters,
} from '@apps/web/components/admin/outreach_observability/runs_drill_dialog';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { cn } from '@apps/web/lib/utils';
import { useAdminOutreachObservability } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type ModelAbTrendDay,
    type ModelCohortStats,
} from '@libraries/typescript/api_client';

// The two experiment cohorts, kept in sync with the backend
// _TV_EXPERIMENT_COHORT_MODELS. A run with no stamp coalesces to the baseline.
const BASELINE_MODEL = 'claude-opus-4-8';
const TREATMENT_MODEL = 'gpt-5.5';
// Candidate treatment models the comparison column can be switched between.
const TREATMENT_MODEL_OPTIONS = [
    'gpt-5.5',
    'gpt-5.6-sol',
    'gpt-5.6-terra',
    'gpt-5.6-luna',
];

const BASELINE_COLOR = 'var(--chart-1)';
const TREATMENT_COLOR = 'var(--chart-2)';

type Cohort = ModelCohortStats | null;

function findCohort(cohorts: Array<ModelCohortStats>, model: string): Cohort {
    return cohorts.find((cohort) => cohort.model === model) ?? null;
}

function formatMs(ms: number | null): string {
    if (ms === null) {
        return '—';
    }
    const seconds = ms / 1000;
    if (seconds < 60) {
        return `${seconds.toFixed(1)}s`;
    }
    return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

function formatUsd(value: number | null): string {
    if (value === null) {
        return '—';
    }
    return value < 1 ? `$${value.toFixed(3)}` : `$${value.toFixed(2)}`;
}

function formatInt(value: number | null): string {
    return value === null ? '—' : Math.round(value).toLocaleString();
}

function formatRate(rate: number | null): string {
    return rate === null ? '—' : `${(rate * 100).toFixed(0)}%`;
}

function rate(count: number, denominator: number): number | null {
    return denominator > 0 ? count / denominator : null;
}

// Delta of the treatment cohort against the baseline. 'percent' is a percent
// change of a count/amount; 'points' is the percentage-point gap of two rates.
function computeDelta(
    baseline: number | null,
    treatment: number | null,
    deltaFormat: 'percent' | 'points',
): number | null {
    if (baseline === null || treatment === null) {
        return null;
    }
    return deltaFormat === 'points'
        ? (treatment - baseline) * 100
        : sameTimeDeltaPct(treatment, baseline);
}

function DeltaBadge({
    pct,
    goodWhenUp,
    deltaFormat,
}: {
    readonly pct: number | null;
    readonly goodWhenUp: boolean;
    readonly deltaFormat: 'percent' | 'points';
}) {
    if (pct === null) {
        return <span className="text-muted-foreground tabular-nums">—</span>;
    }
    const magnitude =
        deltaFormat === 'points'
            ? `${Math.abs(pct).toFixed(1)}pp`
            : `${Math.abs(pct).toFixed(0)}%`;
    // Tone follows the displayed (rounded) magnitude so a delta that renders
    // as 0 is never colored as a move in either direction.
    const tone = deltaTone(
        Number.parseFloat(
            Math.abs(pct).toFixed(deltaFormat === 'points' ? 1 : 0),
        ) * Math.sign(pct),
        goodWhenUp,
    );
    if (tone === 'neutral') {
        return (
            <span className="text-muted-foreground tabular-nums">
                {magnitude}
            </span>
        );
    }
    return (
        <span
            className={cn(
                'tabular-nums',
                tone === 'good'
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-red-600 dark:text-red-400',
            )}
        >
            {pct > 0 ? '▲' : '▼'}
            {magnitude}
        </span>
    );
}

type RowSpec = {
    readonly label: string;
    readonly primary: (cohort: Cohort) => string;
    readonly sub?: (cohort: Cohort) => string | null;
    // Numeric basis for the treatment-vs-baseline delta; omit to hide it.
    readonly value?: (cohort: Cohort) => number | null;
    readonly goodWhenUp: boolean;
    readonly deltaFormat: 'percent' | 'points';
};

function ComparisonCard({
    title,
    description,
    rows,
    baseline,
    treatment,
    baselineModel,
    treatmentModel,
}: {
    readonly title: string;
    readonly description: string;
    readonly rows: Array<RowSpec>;
    readonly baseline: Cohort;
    readonly treatment: Cohort;
    readonly baselineModel: string;
    readonly treatmentModel: string;
}) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-muted-foreground text-left text-xs uppercase">
                                <th className="py-2 pr-4 font-medium">
                                    Metric
                                </th>
                                <th className="px-4 py-2 text-right font-medium">
                                    {baselineModel}
                                </th>
                                <th className="px-4 py-2 text-right font-medium">
                                    {treatmentModel}
                                </th>
                                <th className="py-2 pl-4 text-right font-medium">
                                    Δ
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const baselineValue = row.value
                                    ? row.value(baseline)
                                    : null;
                                const treatmentValue = row.value
                                    ? row.value(treatment)
                                    : null;
                                const baselineSub = row.sub
                                    ? row.sub(baseline)
                                    : null;
                                const treatmentSub = row.sub
                                    ? row.sub(treatment)
                                    : null;
                                return (
                                    <tr
                                        key={row.label}
                                        className="border-t align-top"
                                    >
                                        <td className="py-2 pr-4">
                                            {row.label}
                                        </td>
                                        <td className="px-4 py-2 text-right tabular-nums">
                                            <div>{row.primary(baseline)}</div>
                                            {baselineSub ? (
                                                <div className="text-muted-foreground text-xs">
                                                    {baselineSub}
                                                </div>
                                            ) : null}
                                        </td>
                                        <td className="px-4 py-2 text-right tabular-nums">
                                            <div>{row.primary(treatment)}</div>
                                            {treatmentSub ? (
                                                <div className="text-muted-foreground text-xs">
                                                    {treatmentSub}
                                                </div>
                                            ) : null}
                                        </td>
                                        <td className="py-2 pl-4 text-right">
                                            {row.value ? (
                                                <DeltaBadge
                                                    pct={computeDelta(
                                                        baselineValue,
                                                        treatmentValue,
                                                        row.deltaFormat,
                                                    )}
                                                    goodWhenUp={row.goodWhenUp}
                                                    deltaFormat={
                                                        row.deltaFormat
                                                    }
                                                />
                                            ) : null}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </CardContent>
        </Card>
    );
}

function latencySub(
    p50: number | null,
    p95: number | null,
    measured: number,
): string {
    return `p50 ${formatMs(p50)} · p95 ${formatMs(p95)} · n=${measured}`;
}

const SPEED_ROWS: Array<RowSpec> = [
    {
        label: 'First response ready',
        primary: (cohort) =>
            formatMs(cohort?.first_response_ready_avg_ms ?? null),
        sub: (cohort) =>
            cohort
                ? latencySub(
                      cohort.first_response_ready_p50_ms,
                      cohort.first_response_ready_p95_ms,
                      cohort.first_response_ready_measured_count,
                  )
                : null,
        value: (cohort) => cohort?.first_response_ready_avg_ms ?? null,
        goodWhenUp: false,
        deltaFormat: 'percent',
    },
    {
        label: 'Time to first recommendations',
        primary: (cohort) => formatMs(cohort?.first_recs_avg_ms ?? null),
        sub: (cohort) =>
            cohort
                ? latencySub(
                      cohort.first_recs_p50_ms,
                      cohort.first_recs_p95_ms,
                      cohort.first_recs_measured_count,
                  )
                : null,
        value: (cohort) => cohort?.first_recs_avg_ms ?? null,
        goodWhenUp: false,
        deltaFormat: 'percent',
    },
    {
        label: 'Queue latency',
        primary: (cohort) => formatMs(cohort?.initial_latency_avg_ms ?? null),
        sub: (cohort) =>
            cohort
                ? latencySub(
                      cohort.initial_latency_p50_ms,
                      cohort.initial_latency_p95_ms,
                      cohort.initial_latency_measured_count,
                  )
                : null,
        value: (cohort) => cohort?.initial_latency_avg_ms ?? null,
        goodWhenUp: false,
        deltaFormat: 'percent',
    },
    {
        label: 'Run duration',
        primary: (cohort) => formatMs(cohort?.duration_avg_ms ?? null),
        sub: (cohort) =>
            cohort
                ? latencySub(
                      cohort.duration_p50_ms,
                      cohort.duration_p95_ms,
                      cohort.duration_measured_count,
                  )
                : null,
        value: (cohort) => cohort?.duration_avg_ms ?? null,
        goodWhenUp: false,
        deltaFormat: 'percent',
    },
];

const COST_ROWS: Array<RowSpec> = [
    {
        label: 'Avg cost / run',
        primary: (cohort) => formatUsd(cohort?.avg_cost_usd ?? null),
        sub: (cohort) => (cohort ? `n=${cohort.cost_run_count}` : null),
        value: (cohort) => cohort?.avg_cost_usd ?? null,
        goodWhenUp: false,
        deltaFormat: 'percent',
    },
    {
        label: 'Total cost',
        primary: (cohort) => formatUsd(cohort?.total_cost_usd ?? null),
        value: (cohort) => cohort?.total_cost_usd ?? null,
        goodWhenUp: false,
        deltaFormat: 'percent',
    },
    {
        label: 'Avg input tokens',
        primary: (cohort) => formatInt(cohort?.avg_input_tokens ?? null),
        value: (cohort) => cohort?.avg_input_tokens ?? null,
        goodWhenUp: false,
        deltaFormat: 'percent',
    },
    {
        label: 'Avg output tokens',
        primary: (cohort) => formatInt(cohort?.avg_output_tokens ?? null),
        value: (cohort) => cohort?.avg_output_tokens ?? null,
        goodWhenUp: false,
        deltaFormat: 'percent',
    },
    {
        label: 'Avg LLM calls',
        primary: (cohort) => {
            const value = cohort?.avg_llm_call_count ?? null;
            return value === null ? '—' : value.toFixed(1);
        },
        value: (cohort) => cohort?.avg_llm_call_count ?? null,
        goodWhenUp: false,
        deltaFormat: 'percent',
    },
];

const EFFECTIVENESS_ROWS: Array<RowSpec> = [
    {
        label: 'Got response rate',
        primary: (cohort) =>
            formatRate(
                cohort
                    ? rate(cohort.got_response_count, cohort.num_runs)
                    : null,
            ),
        value: (cohort) =>
            cohort ? rate(cohort.got_response_count, cohort.num_runs) : null,
        goodWhenUp: true,
        deltaFormat: 'points',
    },
    {
        label: 'Got yes rate',
        primary: (cohort) =>
            formatRate(
                cohort ? rate(cohort.got_yes_count, cohort.num_runs) : null,
            ),
        value: (cohort) =>
            cohort ? rate(cohort.got_yes_count, cohort.num_runs) : null,
        goodWhenUp: true,
        deltaFormat: 'points',
    },
    {
        label: 'Callout fill rate',
        primary: (cohort) =>
            formatRate(
                cohort ? rate(cohort.filled_count, cohort.num_runs) : null,
            ),
        value: (cohort) =>
            cohort ? rate(cohort.filled_count, cohort.num_runs) : null,
        goodWhenUp: true,
        deltaFormat: 'points',
    },
    {
        label: 'Shift fill rate',
        primary: (cohort) =>
            formatRate(
                cohort
                    ? rate(
                          cohort.filled_shift_count,
                          cohort.covered_shift_count,
                      )
                    : null,
            ),
        sub: (cohort) =>
            cohort ? `${cohort.covered_shift_count} covered shifts` : null,
        value: (cohort) =>
            cohort
                ? rate(cohort.filled_shift_count, cohort.covered_shift_count)
                : null,
        goodWhenUp: true,
        deltaFormat: 'points',
    },
    {
        label: 'Zero-suggestion rate',
        primary: (cohort) =>
            formatRate(
                cohort
                    ? rate(cohort.zero_suggestion_count, cohort.num_runs)
                    : null,
            ),
        value: (cohort) =>
            cohort ? rate(cohort.zero_suggestion_count, cohort.num_runs) : null,
        goodWhenUp: false,
        deltaFormat: 'points',
    },
];

const RELIABILITY_ROWS: Array<RowSpec> = [
    {
        label: 'Failed rate',
        primary: (cohort) =>
            formatRate(
                cohort ? rate(cohort.failed_count, cohort.num_runs) : null,
            ),
        value: (cohort) =>
            cohort ? rate(cohort.failed_count, cohort.num_runs) : null,
        goodWhenUp: false,
        deltaFormat: 'points',
    },
    {
        label: 'Stale / idle rate',
        primary: (cohort) =>
            formatRate(
                cohort ? rate(cohort.stale_idle_count, cohort.num_runs) : null,
            ),
        value: (cohort) =>
            cohort ? rate(cohort.stale_idle_count, cohort.num_runs) : null,
        goodWhenUp: false,
        deltaFormat: 'points',
    },
    {
        label: 'Tool-failure rate',
        primary: (cohort) =>
            formatRate(
                cohort
                    ? rate(cohort.tool_failure_run_count, cohort.num_runs)
                    : null,
            ),
        value: (cohort) =>
            cohort
                ? rate(cohort.tool_failure_run_count, cohort.num_runs)
                : null,
        goodWhenUp: false,
        deltaFormat: 'points',
    },
    {
        label: 'Compaction rate',
        primary: (cohort) =>
            formatRate(
                cohort
                    ? rate(cohort.compaction_run_count, cohort.num_runs)
                    : null,
            ),
        value: (cohort) =>
            cohort ? rate(cohort.compaction_run_count, cohort.num_runs) : null,
        goodWhenUp: false,
        deltaFormat: 'points',
    },
    {
        label: 'Provider fallback share',
        primary: (cohort) =>
            formatRate(
                cohort
                    ? rate(
                          cohort.fallback_run_count,
                          cohort.fallback_measured_count,
                      )
                    : null,
            ),
        sub: (cohort) =>
            cohort ? `n=${cohort.fallback_measured_count}` : null,
        value: (cohort) =>
            cohort
                ? rate(
                      cohort.fallback_run_count,
                      cohort.fallback_measured_count,
                  )
                : null,
        goodWhenUp: false,
        deltaFormat: 'points',
    },
];

function gradeSummary(cohort: Cohort): string | null {
    if (!cohort) {
        return null;
    }
    const entries = Object.entries(cohort.judge_grade_counts);
    if (entries.length === 0) {
        return null;
    }
    return entries.map(([grade, count]) => `${grade} ${count}`).join(' · ');
}

const QUALITY_ROWS: Array<RowSpec> = [
    {
        label: 'Judged runs',
        primary: (cohort) => formatInt(cohort?.judged_run_count ?? null),
        goodWhenUp: true,
        deltaFormat: 'percent',
    },
    {
        label: 'Judge flag rate',
        primary: (cohort) =>
            formatRate(
                cohort
                    ? rate(cohort.judge_flag_count, cohort.judged_run_count)
                    : null,
            ),
        value: (cohort) =>
            cohort
                ? rate(cohort.judge_flag_count, cohort.judged_run_count)
                : null,
        goodWhenUp: false,
        deltaFormat: 'points',
    },
    {
        label: 'Grade distribution',
        primary: (cohort) => gradeSummary(cohort) ?? '—',
        goodWhenUp: true,
        deltaFormat: 'percent',
    },
];

type TrendPoint = {
    readonly label: string;
    readonly baseline: number | null;
    readonly treatment: number | null;
};

function buildTrendData(
    trend: Array<ModelAbTrendDay>,
    getValue: (row: ModelAbTrendDay) => number | null,
    baselineModel: string,
    treatmentModel: string,
): Array<TrendPoint> {
    const byDay = new Map<string, TrendPoint>();
    for (const row of trend) {
        const existing = byDay.get(row.day) ?? {
            label: format(new Date(`${row.day}T00:00:00`), 'M/d'),
            baseline: null,
            treatment: null,
        };
        const next: TrendPoint =
            row.model === baselineModel
                ? { ...existing, baseline: getValue(row) }
                : row.model === treatmentModel
                  ? { ...existing, treatment: getValue(row) }
                  : existing;
        byDay.set(row.day, next);
    }
    return Array.from(byDay.values());
}

function CohortTrendChart({
    data,
    valueFormatter,
    baselineModel,
    treatmentModel,
}: {
    readonly data: Array<TrendPoint>;
    readonly valueFormatter: (value: number) => string;
    readonly baselineModel: string;
    readonly treatmentModel: string;
}) {
    if (data.length === 0) {
        return (
            <div className="text-muted-foreground flex h-64 items-center justify-center rounded-md border text-sm">
                No data to plot.
            </div>
        );
    }
    return (
        <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ left: 8, right: 8 }}>
                    <CartesianGrid
                        stroke="var(--border)"
                        strokeDasharray="3 3"
                    />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis
                        tick={{ fontSize: 11 }}
                        tickFormatter={valueFormatter}
                        width={56}
                    />
                    <RechartsTooltip
                        formatter={(value) =>
                            typeof value === 'number'
                                ? valueFormatter(value)
                                : `${value}`
                        }
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line
                        dataKey="baseline"
                        name={baselineModel}
                        stroke={BASELINE_COLOR}
                        connectNulls={true}
                        dot={false}
                        isAnimationActive={false}
                    />
                    <Line
                        dataKey="treatment"
                        name={treatmentModel}
                        stroke={TREATMENT_COLOR}
                        connectNulls={true}
                        dot={false}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

type RunsDrill = {
    readonly title: string;
    readonly description?: string;
    readonly metric: KpiRunMetric;
    readonly filters?: RunsDrillFilters;
};

function CohortCountCard({
    cohort,
    model,
    onClick,
}: {
    readonly cohort: Cohort;
    readonly model: string;
    readonly onClick: () => void;
}) {
    return (
        <KpiStatCard
            label={model}
            value={formatInt(cohort?.num_runs ?? 0)}
            sub={`${cohort?.org_count ?? 0} orgs`}
            onClick={onClick}
        />
    );
}

type ModelAbTabProps = OutreachTabProps & {
    readonly onUrgencyChange: (urgency: OutreachTabProps['urgency']) => void;
};

export function ModelAbTab({
    organizationIds,
    urgency,
    onUrgencyChange,
}: ModelAbTabProps) {
    const { data: responseData } = useAdminOutreachObservability(
        {
            sections: ['model_ab'],
            organizationIds,
            urgency,
        },
        { refreshIntervalMs: OUTREACH_REFRESH_INTERVAL_MS },
    );
    const data = responseData?.urgency === urgency ? responseData : undefined;
    const [drill, setDrill] = useState<RunsDrill | null>(null);

    const [selectedTreatment, setSelectedTreatment] = useState<string | null>(
        null,
    );
    const section = data?.model_ab ?? null;
    // The payload names the frozen experiment cohorts; the constants are only
    // a fallback for a stale cached payload from an older deploy. An explicit
    // dropdown selection overrides both.
    const baselineModel = section?.baseline_model ?? BASELINE_MODEL;
    const treatmentModel =
        selectedTreatment ?? section?.treatment_model ?? TREATMENT_MODEL;
    const cohorts = section?.cohorts ?? [];
    const baseline = findCohort(cohorts, baselineModel);
    const treatment = findCohort(cohorts, treatmentModel);
    const otherCohorts = cohorts.filter(
        (cohort) =>
            cohort.model !== baselineModel && cohort.model !== treatmentModel,
    );
    const trend = section?.trend ?? [];

    if (data && section === null) {
        return (
            <div className="text-muted-foreground flex h-40 items-center justify-center rounded-md border text-sm">
                No model A/B data available.
            </div>
        );
    }

    const openRunsDrill = (model: string) =>
        setDrill({
            title: `${model} runs`,
            description: `Outreach runs stamped ${model} over the last ${section?.days ?? 14} days.`,
            metric: 'runs',
            filters: { model, days: section?.days ?? 14 },
        });

    return (
        <div className="flex flex-col gap-4">
            <Card>
                <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex flex-col gap-1.5">
                            <CardTitle>Cohort progress</CardTitle>
                            <CardDescription>
                                Run and org counts per cohort over the last{' '}
                                {section?.days ?? 14} days — the stopping-rule
                                progress. Click a cohort to list its runs.
                            </CardDescription>
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-3">
                            <div className="flex items-center gap-2">
                                <span className="text-muted-foreground text-xs">
                                    Run type
                                </span>
                                <OutreachUrgencySelect
                                    value={urgency}
                                    onValueChange={onUrgencyChange}
                                    ariaLabel="Model A/B run type"
                                />
                            </div>
                            <Select
                                value={treatmentModel}
                                onValueChange={(value) =>
                                    setSelectedTreatment(value)
                                }
                            >
                                <SelectTrigger className="h-8 w-fit gap-1 text-xs">
                                    <span className="text-muted-foreground">
                                        vs
                                    </span>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {TREATMENT_MODEL_OPTIONS.map((option) => (
                                        <SelectItem key={option} value={option}>
                                            {option}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                        <CohortCountCard
                            cohort={baseline}
                            model={baselineModel}
                            onClick={() => openRunsDrill(baselineModel)}
                        />
                        <CohortCountCard
                            cohort={treatment}
                            model={treatmentModel}
                            onClick={() => openRunsDrill(treatmentModel)}
                        />
                    </div>
                    {otherCohorts.length > 0 ? (
                        <p className="text-muted-foreground mt-3 text-xs">
                            Other models:{' '}
                            {otherCohorts
                                .map(
                                    (cohort) =>
                                        `${cohort.model} (${cohort.num_runs} runs)`,
                                )
                                .join(', ')}
                        </p>
                    ) : null}
                </CardContent>
            </Card>
            <ComparisonCard
                title="Speed"
                description="Latency at outcome parity — the primary metric. Lower is better; deltas are treatment vs baseline."
                rows={SPEED_ROWS}
                baseline={baseline}
                treatment={treatment}
                baselineModel={baselineModel}
                treatmentModel={treatmentModel}
            />
            <ComparisonCard
                title="Cost"
                description="Cost and token usage per run. Lower is better."
                rows={COST_ROWS}
                baseline={baseline}
                treatment={treatment}
                baselineModel={baselineModel}
                treatmentModel={treatmentModel}
            />
            <ComparisonCard
                title="Effectiveness"
                description="Outreach outcomes — the guardrail that speed must not regress. Higher is better except zero-suggestion rate."
                rows={EFFECTIVENESS_ROWS}
                baseline={baseline}
                treatment={treatment}
                baselineModel={baselineModel}
                treatmentModel={treatmentModel}
            />
            <ComparisonCard
                title="Reliability"
                description="Run failures, stalls, tool errors, and provider fallback share. Lower is better."
                rows={RELIABILITY_ROWS}
                baseline={baseline}
                treatment={treatment}
                baselineModel={baselineModel}
                treatmentModel={treatmentModel}
            />
            <ComparisonCard
                title="Quality"
                description="LLM-judge grades. Read cautiously — the judge is a Claude-family model."
                rows={QUALITY_ROWS}
                baseline={baseline}
                treatment={treatment}
                baselineModel={baselineModel}
                treatmentModel={treatmentModel}
            />
            <Card>
                <CardHeader>
                    <CardTitle>Trend</CardTitle>
                    <CardDescription>
                        Per-cohort daily headline metrics (America/New_York
                        days).
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-6">
                    <div>
                        <div className="text-muted-foreground mb-2 text-xs font-medium uppercase">
                            Avg first response ready
                        </div>
                        <CohortTrendChart
                            data={buildTrendData(
                                trend,
                                (row) => row.avg_first_response_ready_ms,
                                baselineModel,
                                treatmentModel,
                            )}
                            valueFormatter={formatMs}
                            baselineModel={baselineModel}
                            treatmentModel={treatmentModel}
                        />
                    </div>
                    <div>
                        <div className="text-muted-foreground mb-2 text-xs font-medium uppercase">
                            Got response rate
                        </div>
                        <CohortTrendChart
                            data={buildTrendData(
                                trend,
                                (row) =>
                                    rate(row.got_response_count, row.num_runs),
                                baselineModel,
                                treatmentModel,
                            )}
                            valueFormatter={(value) =>
                                `${(value * 100).toFixed(0)}%`
                            }
                            baselineModel={baselineModel}
                            treatmentModel={treatmentModel}
                        />
                    </div>
                </CardContent>
            </Card>
            <p className="text-muted-foreground text-xs">
                Cohorts are intent-to-treat by the stamped outreach model; a run
                with no stamp counts toward {baselineModel}. Provider-fallback
                share is shown under Reliability. Judge grades come from a
                Claude-family judge, so read {treatmentModel} quality
                cautiously.
            </p>
            <RunsDrillDialog
                open={drill !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setDrill(null);
                    }
                }}
                title={drill?.title ?? ''}
                description={drill?.description}
                metric={drill?.metric ?? 'runs'}
                organizationIds={organizationIds}
                urgency={urgency}
                filters={drill?.filters}
            />
        </div>
    );
}
